import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
} from '@mui/material';

interface AgencyFlagControlProps {
  isAgency: boolean;
  reporterName?: string;
  onEnable: () => void;
  onDisable: () => void;
}

export const AgencyFlagControl = ({
  isAgency, reporterName, onEnable, onDisable,
}: AgencyFlagControlProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!isAgency) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 1.5, alignItems: 'center' }}>
          Agencies can register multiple individual sub-reporters under a single resource — each gets their own login email while sharing the agency's RB resource.
        </Alert>
        <Alert severity="warning" sx={{ mb: 2.5, alignItems: 'center' }}>
          Changes take effect on the reporter's next login or when the JWT token is refreshed.
        </Alert>
        <Button
          variant="outlined"
          size="small"
          onClick={onEnable}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          Mark as agency
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="text"
          color="error"
          size="small"
          onClick={() => setConfirmOpen(true)}
          sx={{
            textTransform: 'none',
            fontWeight: 400,
            minWidth: 'auto',
            p: 0,
            '&:hover': { textDecoration: 'underline', background: 'none' },
          }}
        >
          Remove agency status
        </Button>
      </Box>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Remove agency status{reporterName ? ` for ${reporterName}` : ''}?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Removing the agency designation will permanently:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <Typography component="li" variant="body2">Remove all authorized sub-reporter emails from this agency</Typography>
            <Typography component="li" variant="body2">Delete all passkeys registered by sub-reporters of this agency</Typography>
            <Typography component="li" variant="body2">Sub-reporters will immediately lose the ability to log in</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { setConfirmOpen(false); onDisable(); }}
          >
            Remove agency status
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
